import { computed, onBeforeUnmount, ref, shallowRef, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  compileMatrix,
  countShownFillableFields,
  creationReadiness,
  createEmptyAnswers,
  editedCellNodeIds,
  exceedsAnswersMaxBytes,
  extraCellNodeId,
  fillableMatrixProblem,
  listWrittenFieldIds,
  newRowsStart,
  repeatedElementsMessage,
  resolveFieldAccess,
  ruleCycleMessage,
  revealedRequiredFieldIds,
  selectionAfterRowShift,
  withExtraRowAppended,
  withExtraRowCellValue,
  withFieldValue,
  withNewExtraRowRemoved,
  withRuleLockedValuesRestored,
  type CompiledForm,
  type AnswersBody,
  type FieldValue,
  type MatrixAnswers,
  type ValidationIssue,
} from '@record-fill';
import {
  createGeneratedRecord,
  editGeneratedRecord,
  getGeneratedRecordConflictCode,
  getGeneratedRecordConflictMessage,
  getGeneratedRecordForm,
  getGeneratedRecordIssues,
  getGeneratedRecordTitleRefusal,
  getGenerationForm,
  getGenerationRefusalMessage,
  GENERATED_RECORD_NOT_FOUND_MESSAGE,
  RECORD_TYPE_UPLOAD_NAME,
  respondGeneratedRecord,
  type GeneratedRecordCorrectionAnswers,
  type GeneratedRecordForm,
  type GeneratedRecordTitleRefusal,
} from '@/entities/GeneratedRecord';
import { useAuthStore } from '@/entities/Session';
import {
  clearBreadcrumbTrail,
  extractApiErrorMessage,
  extractApiFieldErrors,
  isNotFoundError,
  setBreadcrumbTrail,
  useCustomToast,
} from '@/shared/lib';
import { buildAnswersSnapshot } from './build-answers-snapshot';
import { buildCorrectionSnapshot } from './correction-answers';
import { collectExtraCellNodes } from './extra-cell-nodes';
import { buildMatrixTitle } from './matrix-title';
import { createPatternGuard } from './pattern-guard';
import { createWorkerPatternRunner, type PatternRunner } from './pattern-runner';
import { buildGenerationTitles, buildRecordLine, needsTypedIndexer } from './record-indexing';
import {
  buildGenerateRecordTrail,
  EDIT_RECORD_BREADCRUMB,
  FILL_RECORD_BREADCRUMB,
  QUALITY_RECORDS_BREADCRUMB,
  VIEW_RECORD_BREADCRUMB,
} from './record-fill.breadcrumb';
import { matrixPageLocation } from './record-fill.return-target';
import { dedupeIssues, issuesAfterRowRemoval, splitServerIssues } from './save-issues';
import { useCorrectionMode } from './useCorrectionMode';
import { useRecordAttachments, type PendingAttachmentsSave } from './useRecordAttachments';
import { useRecordLock } from './useRecordLock';

export const LOAD_ERROR_TITLE = 'Não foi possível carregar a matriz.';
export const GENERATION_REFUSED_TITLE = 'Não é possível gerar o Registro.';
export const SAVE_SUCCESS_TITLE = 'Registro salvo com sucesso!';
export const SAVE_ERROR_TITLE = 'Erro ao salvar Registro. Tente novamente.';
export const TITLE_DUPLICATED_SAVE_TITLE = 'Não é possível salvar o Registro.';
export const HIDDEN_ISSUES_TITLE = 'Há campo escondido por regra com valor inválido.';
export const ROW_ISSUES_TITLE = 'Há linha acrescentada que não pode ser salva.';
export const OPEN_ERROR_TITLE = 'Não foi possível abrir o Registro.';
export const LOCK_LOST_MESSAGE =
  'Este Registro não está mais aberto para você. Abra o Registro novamente para continuar.';
export const LOCK_RELEASED_TITLE = 'Registro liberado.';
export const STALE_RECORD_MESSAGE =
  'Este Registro recebeu uma resposta mais recente. Abra o Registro novamente para continuar.';
export const UPLOAD_HAS_PDF_MESSAGE = 'Este Registro já recebeu um PDF e não pode mais ser editado.';
export const EDIT_NOT_UPLOAD_MESSAGE = 'Somente Registro do tipo Upload é editado por esta tela.';
export const EDIT_NO_CHANGES_MESSAGE = 'Nenhuma alteração para salvar.';
export const ANSWERS_TOO_LARGE_TITLE = 'Não é possível salvar o Registro.';
export const ANSWERS_TOO_LARGE_MESSAGE =
  'A resposta ficou grande demais para salvar de uma vez. Salve parte das linhas agora e acrescente o resto numa nova resposta.';
export const INDEXER_CHECK_ERROR_TITLE = 'Não foi possível conferir o indexador do registro. Tente novamente.';

function describeIssue(form: CompiledForm, issue: ValidationIssue): string {
  const label = form.fields[issue.fieldId]?.label ?? '';
  return label.length > 0 ? `${label}: ${issue.message}` : issue.message;
}

// A compiled issue of an added-row cell carries the model field id plus the position; on the
// sheet the control answers by the display node id, so focus and visibility go through it.
function sheetTargetId(issue: ValidationIssue): string {
  return issue.cell && issue.cell.columnIndex !== undefined
    ? extraCellNodeId(issue.cell.tableId, issue.cell.rowIndex, issue.cell.columnIndex)
    : issue.fieldId;
}

interface RefusedIndexer {
  text: string;
  message: string;
}

type SaveBody =
  | { answers: AnswersBody; isCorrection?: false }
  | { answers: GeneratedRecordCorrectionAnswers; isCorrection: true };

interface GenerateRecordPageOptions {
  patternRunner?: PatternRunner;
  // Present only on the fill, edit and view routes: the page then answers an existing record instead of
  // creating one. The route decides it once, because the routes never share a component instance.
  recordId?: Ref<string>;
  // The revision folder the person came from; without it the way back is the code's matrix page.
  returnFolderCode?: string | null;
  // The sheet edit of an Upload record: no lock, guarded by the response number alone.
  editing?: boolean;
  // Read only: no lock, so it never turns the record "Em uso", and nothing to save.
  viewing?: boolean;
  identifier?: Ref<string>;
  indexer?: Ref<string>;
}

function isUploadForm(form: GeneratedRecordForm | null): boolean {
  return form?.matrix.recordTypeName === RECORD_TYPE_UPLOAD_NAME;
}

export function useGenerateRecordPage(
  matrixId: Ref<string>,
  prefix: Ref<string>,
  options: GenerateRecordPageOptions = {}
) {
  const patternGuard = createPatternGuard(options.patternRunner ?? createWorkerPatternRunner());
  const router = useRouter();
  const authStore = useAuthStore();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const recordId = options.recordId;
  const identifier = options.identifier;
  const typedIndexer = computed(() => options.indexer?.value ?? '');
  const isFilling = recordId !== undefined;
  const returnFolderCode = options.returnFolderCode ?? null;
  const isEditing = isFilling && options.editing === true;
  const isViewing = isFilling && options.viewing === true;
  const lock = isFilling && !isEditing && !isViewing
    ? useRecordLock({
        onRestoredFromCache: () => {
          load();
        },
        onLost: () =>
          showErrorToast({ title: LOCK_RELEASED_TITLE, type: 'error', message: LOCK_LOST_MESSAGE }),
        // The sheet answers the response read at opening; a lock taken back over a newer one would
        // only lead to a refused save, so it is given back at once.
        onReclaimed: (opened) => {
          if (opened.record.lastResponseNumber === baseResponseNumber) return;
          lock?.release();
          showErrorToast({
            title: LOCK_RELEASED_TITLE,
            type: 'error',
            message: STALE_RECORD_MESSAGE,
          });
        },
      })
    : null;

  const form = shallowRef<GeneratedRecordForm | null>(null);
  const loading = ref(true);
  const answers = ref<MatrixAnswers>(createEmptyAnswers(0));
  const selectedFieldId = ref<string | null>(null);
  const saveAttempted = ref(false);
  const serverIssues = ref<ValidationIssue[]>([]);
  const saving = ref(false);
  const stageModalOpen = ref(false);
  const indexerError = ref<string | null>(null);
  const indexerRefused = ref(false);
  const indexerChecking = ref(false);
  // The query keeps a title the server refused at Salvar, so the next Salvar with that same text
  // reopens the modal instead of sending it again.
  let refusedIndexer: RefusedIndexer | null = null;
  let latestRequest = 0;
  let baseResponseNumber = 0;
  const openedAnswers = shallowRef<MatrixAnswers | null>(null);
  const attachments = useRecordAttachments({
    mode: attachmentsMode(),
    form,
    lockToken: () => lock?.token() ?? null,
  });
  const correction = useCorrectionMode({
    recordId,
    answers,
    openedAnswers,
    canToggle: () =>
      !isViewing &&
      form.value !== null &&
      !isUploadForm(form.value) &&
      !saving.value &&
      !stageModalOpen.value,
  });

  function attachmentsMode() {
    if (isViewing) return 'view';
    if (isEditing) return 'edit';
    return isFilling ? 'fill' : 'generate';
  }

  const title = computed(() => (form.value ? buildMatrixTitle(form.value.matrix) : ''));
  // The URL prefix only finds the way back before the form arrives; after that the matrix the
  // record is created on decides where the person returns, even on a hand-typed URL.
  const matrixPrefix = computed(() => form.value?.matrix.prefix ?? prefix.value);
  const stages = computed(() => form.value?.stages ?? []);
  const showExportPdf = computed(() => !isUploadForm(form.value));
  const recordLine = computed(() =>
    form.value ? buildRecordLine(form.value, answers.value.values, typedIndexer.value) : null
  );
  // A refused title brings the modal back over the sheet, and it stays open while the person retypes,
  // although the query still holds the refused text.
  const indexerModalOpen = computed(
    () =>
      form.value !== null &&
      needsTypedIndexer(form.value) &&
      (typedIndexer.value.trim() === '' || indexerRefused.value)
  );

  const writtenFieldIds = computed(() =>
    isFilling && openedAnswers.value
      ? listWrittenFieldIds(openedAnswers.value, answers.value)
      : null
  );

  // The cells the author edits in the own rows, validated in full like a written field; the rest of
  // those rows is checked only for shape, as the server does.
  const editedCellIds = computed(() => {
    const opened = openedAnswers.value;
    if (!isFilling || !opened) return [];
    return editedCellNodeIds(opened, answers.value);
  });

  // A response may change answered fields (the author's own, or any on an Upload edit), so a change
  // can bring an empty required field into view; it is charged as in a correction, the same set the
  // server adds back.
  const validatedFieldIds = computed(() => {
    const written = writtenFieldIds.value && [...writtenFieldIds.value, ...editedCellIds.value];
    const loaded = form.value;
    if (!written || !loaded || !openedAnswers.value) return written;
    const revealed = revealedRequiredFieldIds({
      structure: loaded.structure,
      rules: loaded.rules,
      opened: openedAnswers.value,
      corrected: answers.value,
    });
    return [...new Set([...written, ...revealed])];
  });

  // Creation sends every editable field, so a sheet left as it opened is still a record to create.
  const hasChanges = computed(() => {
    if (isViewing) return false;
    if (!isFilling) return true;
    const opened = openedAnswers.value;
    if (!opened) return false;
    if ((writtenFieldIds.value?.length ?? 0) > 0) return true;
    if (correction.changeCount.value > 0) return true;
    if (editedCellIds.value.length > 0) return true;
    return Object.entries(answers.value.tables).some(
      ([tableId, entry]) => entry.extraRows.length !== (opened.tables[tableId]?.extraRows.length ?? 0)
    );
  });

  const compiled = computed<CompiledForm | null>(() => {
    const correctionOptions = correction.compileOptions();
    const loaded = correctionOptions ? correction.form.value : form.value;
    if (!loaded) return null;
    return compileMatrix({
      structure: loaded.structure,
      rules: loaded.rules,
      permissions: loaded.permissions,
      subject: loaded.subject,
      answers: answers.value,
      options: correctionOptions
        ? { ...correctionOptions, patternTest: patternGuard.patternTest }
        : {
            frozenFieldIds: [...loaded.frozenFieldIds, ...loaded.lockedFieldIds],
            frozenExtraRowCounts: loaded.frozenExtraRowCounts,
            reopenedExtraRows: loaded.reopenedExtraRows,
            patternTest: patternGuard.patternTest,
            ...(validatedFieldIds.value ? { validatedFieldIds: validatedFieldIds.value } : {}),
          },
      titles: buildGenerationTitles(loaded, answers.value.values, typedIndexer.value),
    });
  });

  // A field a rule hides or disables shows its saved value again, as the save keeps it, so a rule
  // that field triggers shows what the save will keep. One synchronous watch catches every way the
  // answers change (typing, loading, entering a correction) before anything reads them. The
  // correction form stays while a correction change waits for Salvar, and that save goes out with
  // its wider permissions, so its access decides which new rows can still be written.
  watch(
    [answers, openedAnswers, form, () => correction.form.value],
    () => {
      const loaded = correction.form.value ?? form.value;
      if (!loaded?.rules?.rules.length) return;
      const next = withRuleLockedValuesRestored({
        structure: loaded.structure,
        rules: loaded.rules,
        access: resolveFieldAccess(loaded.permissions, loaded.subject),
        saved: openedAnswers.value,
        answers: answers.value,
      });
      if (next === answers.value) return;
      const before = answers.value;
      answers.value = next;
      forgetDroppedRows(before, next);
    },
    { flush: 'sync' }
  );

  // A rule that leaves no column to write drops the new rows, as the save does; their server issues
  // and highlight go with them, the way removeExtraRow handles a row the person takes out.
  function forgetDroppedRows(before: MatrixAnswers, after: MatrixAnswers): void {
    for (const [tableId, entry] of Object.entries(before.tables)) {
      const kept = after.tables[tableId]?.extraRows.length ?? 0;
      if (entry.extraRows.length <= kept) continue;
      serverIssues.value = serverIssues.value.filter(
        (issue) => !(issue.cell?.tableId === tableId && issue.cell.rowIndex >= kept)
      );
      selectedFieldId.value = selectionAfterRowShift(selectedFieldId.value, tableId, kept);
    }
  }

  // What this subject sees, added rows included: it differs from the builder's matrix count on purpose.
  const totalFields = computed(() => (compiled.value ? countShownFillableFields(compiled.value) : 0));

  // Anchors are owed at creation (and at archiving); a later response is not refused for them.
  const anchorReadiness = computed(() =>
    compiled.value && !isFilling ? creationReadiness(compiled.value) : null
  );

  // An empty required anchor already reads "Campo obrigatório."; the anchor message on top of it
  // would say the same thing twice on one field.
  const anchorIssues = computed<ValidationIssue[]>(() => {
    const compiledFieldIds = new Set((compiled.value?.issues ?? []).map((issue) => issue.fieldId));
    return (anchorReadiness.value?.issues ?? []).filter((issue) => !compiledFieldIds.has(issue.fieldId));
  });

  const extraIssues = computed<ValidationIssue[]>(() => [
    ...(saveAttempted.value ? anchorIssues.value : []),
    ...serverIssues.value,
  ]);

  function resetSheetState(): void {
    selectedFieldId.value = null;
    saveAttempted.value = false;
    serverIssues.value = [];
    stageModalOpen.value = false;
    indexerError.value = null;
    indexerRefused.value = false;
    refusedIndexer = null;
  }

  function clearIndexerError(): void {
    indexerError.value = null;
  }

  function acceptIndexer(): void {
    indexerError.value = null;
    indexerRefused.value = false;
    refusedIndexer = null;
  }

  function reopenIndexer(message: string): void {
    refusedIndexer = { text: typedIndexer.value.trim(), message };
    indexerError.value = message;
    indexerRefused.value = true;
  }

  // Reopened over a filled sheet after a refused Salvar, the modal only closes: leaving the page
  // there would throw away every answer the person typed.
  async function cancelIndexer(): Promise<void> {
    if (indexerRefused.value) {
      indexerRefused.value = false;
      indexerError.value = null;
      return;
    }
    await cancel();
  }

  function indexerRefusalMessage(error: unknown): string | null {
    const titleRefusal = getGeneratedRecordTitleRefusal(error);
    if (titleRefusal) return titleRefusal.indexerError ?? titleRefusal.message;
    return extractApiFieldErrors(error).indexer?.[0] ?? null;
  }

  // The form is asked again only to let the server refuse the title before the sheet is filled;
  // the sheet already open stays as it is.
  async function checkIndexer(typed: string): Promise<boolean> {
    if (indexerChecking.value) return false;
    const request = latestRequest;
    indexerChecking.value = true;
    try {
      await getGenerationForm(Number(matrixId.value), { indexer: typed });
      return request === latestRequest;
    } catch (error) {
      if (request !== latestRequest) return false;
      const refusal = indexerRefusalMessage(error);
      if (refusal !== null) {
        indexerError.value = refusal;
        return false;
      }
      const message = extractApiErrorMessage(error);
      showErrorToast({ title: INDEXER_CHECK_ERROR_TITLE, type: 'error', ...(message ? { message } : {}) });
      return false;
    } finally {
      indexerChecking.value = false;
    }
  }

  function generatedRecordsTab(targetPrefix: string) {
    return matrixPageLocation(targetPrefix, returnFolderCode, { generatedRecords: true });
  }

  async function fetchForm(): Promise<GeneratedRecordForm | null> {
    if ((isEditing || isViewing) && recordId) return getGeneratedRecordForm(Number(recordId.value));
    if (lock && recordId) return lock.open(Number(recordId.value));
    const typed = identifier?.value ?? '';
    return typed.trim() === ''
      ? getGenerationForm(Number(matrixId.value))
      : getGenerationForm(Number(matrixId.value), { identifier: typed });
  }

  async function reportLoadFailure(error: unknown): Promise<void> {
    if (!isFilling) {
      const refusal = getGenerationRefusalMessage(error);
      const message = refusal ?? extractApiErrorMessage(error);
      const title = refusal === null ? LOAD_ERROR_TITLE : GENERATION_REFUSED_TITLE;
      showErrorToast({ title, type: 'error', ...(message ? { message } : {}) });
      await router.replace(matrixPageLocation(prefix.value, returnFolderCode));
      return;
    }
    const message =
      getGeneratedRecordConflictMessage(error) ??
      getGenerationRefusalMessage(error) ??
      (isNotFoundError(error) ? GENERATED_RECORD_NOT_FOUND_MESSAGE : null);
    showErrorToast({ title: OPEN_ERROR_TITLE, type: 'error', ...(message ? { message } : {}) });
    await router.replace(generatedRecordsTab(prefix.value));
  }

  // A stale list or a hand-typed URL still lands here after the first PDF, or on another type.
  // The server reads this form on purpose, so the record stays readable, and refuses only the save:
  // without this check the person would type the whole edit before learning it cannot be saved.
  function sheetEditRefusal(loaded: GeneratedRecordForm): string | null {
    if (!isUploadForm(loaded)) return EDIT_NOT_UPLOAD_MESSAGE;
    if (loaded.record?.hasPdfResponse === true) return UPLOAD_HAS_PDF_MESSAGE;
    const problem = fillableMatrixProblem(loaded.structure, loaded.rules);
    if (!problem) return null;
    return problem.kind === 'repeated-ids'
      ? repeatedElementsMessage(problem.labelGroups)
      : ruleCycleMessage(problem.labelGroups);
  }

  async function refuseSheetEdit(loaded: GeneratedRecordForm, message: string): Promise<void> {
    showErrorToast({ title: OPEN_ERROR_TITLE, type: 'error', message });
    await router.replace(generatedRecordsTab(loaded.matrix.prefix));
  }

  function trailEnd() {
    if (isEditing) return EDIT_RECORD_BREADCRUMB;
    if (isViewing) return VIEW_RECORD_BREADCRUMB;
    return isFilling ? FILL_RECORD_BREADCRUMB : undefined;
  }

  async function load(): Promise<void> {
    // Only the newest request may publish: a late failure of the previous id would send the
    // right form back to the matrix page.
    const request = ++latestRequest;

    loading.value = true;
    form.value = null;
    correction.reset();
    resetSheetState();
    setBreadcrumbTrail([QUALITY_RECORDS_BREADCRUMB]);

    try {
      const loaded = await fetchForm();
      if (request !== latestRequest || loaded === null) return;
      // Set only after the newest-request check: a late read of the previous record would leave
      // its response number on the sheet now shown, and the save would pass the stale check.
      baseResponseNumber = loaded.record?.lastResponseNumber ?? 0;
      const refusal = isEditing ? sheetEditRefusal(loaded) : null;
      if (refusal) {
        await refuseSheetEdit(loaded, refusal);
        return;
      }
      answers.value = { ...createEmptyAnswers(loaded.matrix.id), ...loaded.answers };
      openedAnswers.value = answers.value;
      form.value = loaded;
      setBreadcrumbTrail(
        buildGenerateRecordTrail(matrixPrefix.value, title.value, trailEnd(), returnFolderCode)
      );
    } catch (error) {
      if (request !== latestRequest) return;
      await reportLoadFailure(error);
    } finally {
      if (request === latestRequest) {
        loading.value = false;
      }
    }
  }

  function dropServerIssues(fieldId: string): void {
    // A server issue answers the value that was sent; once the person changes it, the local
    // compilation is the judge again.
    if (serverIssues.value.some((issue) => issue.fieldId === fieldId)) {
      serverIssues.value = serverIssues.value.filter((issue) => issue.fieldId !== fieldId);
    }
  }

  function setValue(fieldId: string, value: FieldValue): void {
    answers.value = withFieldValue(answers.value, fieldId, value);
    dropServerIssues(fieldId);
  }

  function setCellValue(tableId: string, rowIndex: number, columnIndex: number, value: FieldValue): void {
    answers.value = withExtraRowCellValue(answers.value, tableId, rowIndex, columnIndex, value);
    dropServerIssues(extraCellNodeId(tableId, rowIndex, columnIndex));
  }

  // "+" drops a highlight on this table's new rows, so a "−" right after it undoes the "+" instead
  // of taking the filled row that was highlighted before.
  function addExtraRow(tableId: string): void {
    const current = compiled.value;
    answers.value = withExtraRowAppended(answers.value, tableId);
    if (current) {
      selectedFieldId.value = selectionAfterRowShift(selectedFieldId.value, tableId, newRowsStart(current, tableId));
    }
  }

  function removeExtraRow(tableId: string, rowIndex: number): void {
    const current = compiled.value;
    if (!current) return;
    const next = withNewExtraRowRemoved(current, answers.value, tableId, rowIndex);
    if (next === answers.value) return;
    answers.value = next;
    serverIssues.value = issuesAfterRowRemoval(serverIssues.value, tableId, rowIndex);
    selectedFieldId.value = selectionAfterRowShift(selectedFieldId.value, tableId, rowIndex);
  }

  function locateField(fieldId: string): void {
    selectedFieldId.value = fieldId;
    document
      .querySelector(`[data-testid="fill-field-${fieldId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // A field the normal sheet locks again after the mode is left still carries its correction, so
  // the correction body is built from the answers and never from the compiled sheet.
  function buildSaveBody(current: CompiledForm): SaveBody {
    const stored = correction.stored.value;
    return stored
      ? { answers: buildCorrectionSnapshot(stored, answers.value), isCorrection: true }
      : {
          answers: buildAnswersSnapshot(
            current,
            answers.value,
            writtenFieldIds.value ?? undefined,
            openedAnswers.value ?? undefined
          ),
        };
  }

  function reportSheetIssues(current: CompiledForm, issues: ValidationIssue[]): void {
    // The engine still checks the shape of a field a rule hides, and the sheet does not draw it:
    // without the toast, Salvar would do nothing and say nothing.
    const extraNodes = collectExtraCellNodes(current);
    const isOnSheet = (issue: ValidationIssue): boolean => {
      const targetId = sheetTargetId(issue);
      return (
        current.fields[targetId]?.state.visible === true || extraNodes[targetId]?.state.visible === true
      );
    };
    const hidden = issues.filter((issue) => !isOnSheet(issue));
    const shown = issues.filter(isOnSheet);
    if (shown.length > 0) locateField(sheetTargetId(shown[0]));
    const isRowIssue = (issue: ValidationIssue) => issue.cell !== undefined && issue.cell.columnIndex === undefined;
    const rowIssues = hidden.filter(isRowIssue);
    const fieldIssues = hidden.filter((issue) => !isRowIssue(issue));
    if (rowIssues.length > 0) {
      showErrorToast({
        title: ROW_ISSUES_TITLE,
        type: 'error',
        message: [...new Set(rowIssues.map((issue) => issue.message))].join(' '),
      });
    }
    if (fieldIssues.length > 0) {
      showErrorToast({
        title: HIDDEN_ISSUES_TITLE,
        type: 'error',
        message: fieldIssues.map((issue) => describeIssue(current, issue)).join(' '),
      });
    }
  }

  async function save(): Promise<void> {
    if (attachments.busy.value) return;
    // A pattern still being checked passes for now; Salvar decides only on the real answers.
    if (patternGuard.hasPending()) await patternGuard.settled();
    const current = compiled.value;
    if (!current || attachments.busy.value) return;
    if (!hasChanges.value) {
      if (isEditing) {
        showErrorToast({ title: SAVE_ERROR_TITLE, type: 'error', message: EDIT_NO_CHANGES_MESSAGE });
      }
      return;
    }

    // The sheet paints the compiled errors only from the first Salvar on, so a sheet full of
    // empty required fields does not open red; after that the errors follow every edit live.
    saveAttempted.value = true;
    serverIssues.value = [];
    const issues = dedupeIssues([...current.issues, ...(anchorReadiness.value?.issues ?? [])]);

    if (issues.length > 0) {
      reportSheetIssues(current, issues);
      return;
    }

    // The server refuses answers past this size in the DTO validation, with a 400 the sheet shows only
    // as a title; checking before the stage modal spares the person a stage choice that could only fail.
    if (exceedsAnswersMaxBytes(buildSaveBody(current).answers)) {
      showErrorToast({ title: ANSWERS_TOO_LARGE_TITLE, type: 'error', message: ANSWERS_TOO_LARGE_MESSAGE });
      return;
    }

    if (form.value && needsTypedIndexer(form.value) && refusedIndexer?.text === typedIndexer.value.trim()) {
      indexerError.value = refusedIndexer.message;
      indexerRefused.value = true;
      return;
    }

    stageModalOpen.value = true;
  }

  function closeStageModal(): void {
    stageModalOpen.value = false;
  }

  async function submitAnswers(
    stageId: number,
    loaded: GeneratedRecordForm,
    body: SaveBody,
    token: string | null,
    pendingSave: PendingAttachmentsSave
  ): Promise<void> {
    if (isEditing && recordId) {
      // canToggle keeps the correction mode off on an Upload record, so this is unreachable; sending
      // a correction body down the edit route would store it as a plain response.
      if (body.isCorrection === true) throw new Error('The sheet edit never carries a correction.');
      await editGeneratedRecord(Number(recordId.value), {
        baseResponseNumber,
        stageId,
        answers: body.answers,
      });
      return;
    }
    if (lock && recordId && token !== null) {
      await respondGeneratedRecord(Number(recordId.value), {
        inUseToken: token,
        baseResponseNumber,
        stageId,
        ...body,
      });
      // The server freed the lock in the same transaction that wrote the response.
      lock.forget(token);
      return;
    }
    // The number the form was identified with, never the query: the titles on the sheet belong to it.
    const identification = loaded.identification;
    await createGeneratedRecord({
      matrixId: loaded.matrix.id,
      stageId,
      answers: body.answers,
      ...(identification ? { identifier: identification.identifier } : {}),
      ...(needsTypedIndexer(loaded) ? { indexer: typedIndexer.value.trim() } : {}),
      ...pendingSave.link,
    });
    pendingSave.linked();
  }

  function reportConflict(error: unknown, token: string | null): boolean {
    const conflictMessage = isFilling ? getGeneratedRecordConflictMessage(error) : null;
    if (conflictMessage === null) return false;
    const lockLost = getGeneratedRecordConflictCode(error) === 'GENERATED_RECORD_LOCK_MISMATCH';
    if (lockLost && token !== null) lock?.forget(token);
    showErrorToast({ title: SAVE_ERROR_TITLE, type: 'error', message: conflictMessage });
    return true;
  }

  function showServerIssues(
    current: CompiledForm,
    fieldIssues: ValidationIssue[],
    lead: string | null,
    trail: string | null,
    title: string = SAVE_ERROR_TITLE
  ): void {
    // Positioned issues arrive already re-keyed to the display node id of the added-row cell.
    const extraNodes = collectExtraCellNodes(current);
    const isOnSheet = (issue: ValidationIssue): boolean =>
      current.fields[issue.fieldId]?.state.visible === true ||
      extraNodes[issue.fieldId]?.state.visible === true;
    const onSheet = fieldIssues.filter(isOnSheet);
    const offSheet = fieldIssues.filter((issue) => !isOnSheet(issue));
    const messages = [lead, ...offSheet.map((issue) => issue.message), trail].filter(
      (message): message is string => message !== null
    );
    showErrorToast({
      title,
      type: 'error',
      ...(messages.length > 0 ? { message: messages.join(' ') } : {}),
    });
    if (onSheet.length > 0) {
      serverIssues.value = onSheet;
      locateField(onSheet[0].fieldId);
    }
  }

  function reportServerIssues(current: CompiledForm, error: unknown): void {
    const issues = getGeneratedRecordIssues(error);
    const { fieldIssues, stageMessage } = splitServerIssues(issues ?? []);
    // A creation refused without issues (an open record of the device, the ERP down) explains
    // itself only in the message; a response keeps reading its conflicts by code, and only a 400
    // refusal of the matrix itself brings its message.
    let refusal: string | null = null;
    if (issues === null) {
      refusal = (isFilling ? getGenerationRefusalMessage(error) : extractApiErrorMessage(error)) ?? null;
    }
    showServerIssues(current, fieldIssues, stageMessage, refusal);
  }

  function reportTitleRefusal(
    current: CompiledForm,
    loaded: GeneratedRecordForm,
    refusal: GeneratedRecordTitleRefusal
  ): void {
    if (!isFilling && needsTypedIndexer(loaded)) {
      reopenIndexer(refusal.indexerError ?? refusal.message);
      return;
    }
    showServerIssues(
      current,
      splitServerIssues(refusal.fieldIssues).fieldIssues,
      refusal.message,
      null,
      TITLE_DUPLICATED_SAVE_TITLE
    );
  }

  function reportSaveFailure(
    current: CompiledForm,
    loaded: GeneratedRecordForm,
    error: unknown,
    token: string | null
  ): void {
    const titleRefusal = getGeneratedRecordTitleRefusal(error);
    if (titleRefusal) {
      reportTitleRefusal(current, loaded, titleRefusal);
      return;
    }
    const indexerFieldError = isFilling ? undefined : extractApiFieldErrors(error).indexer?.[0];
    if (indexerFieldError !== undefined) {
      reopenIndexer(indexerFieldError);
      return;
    }
    if (!reportConflict(error, token)) reportServerIssues(current, error);
  }

  async function confirmStage(stageId: number): Promise<void> {
    const current = compiled.value;
    const loaded = form.value;
    if (saving.value || !current || !loaded || attachments.busy.value) return;

    // A save that lands after the person left the screen (unmount, branch change, another matrix)
    // must not toast, paint issues or navigate on top of the new screen.
    const request = latestRequest;
    const body = buildSaveBody(current);
    const token = lock?.token() ?? null;
    if (lock && token === null) {
      closeStageModal();
      showErrorToast({ title: SAVE_ERROR_TITLE, type: 'error', message: LOCK_LOST_MESSAGE });
      return;
    }
    saving.value = true;
    const pendingSave = attachments.beginSave();
    try {
      await submitAnswers(stageId, loaded, body, token, pendingSave);
      if (request !== latestRequest) return;
      closeStageModal();
      showSuccessToast({ title: SAVE_SUCCESS_TITLE, type: 'success' });
      await router.push(generatedRecordsTab(matrixPrefix.value));
    } catch (error) {
      pendingSave.failed(error);
      if (request !== latestRequest) return;
      closeStageModal();
      reportSaveFailure(current, loaded, error, token);
    } finally {
      saving.value = false;
    }
  }

  async function cancel(): Promise<void> {
    if (isEditing || isViewing) {
      await router.push(generatedRecordsTab(matrixPrefix.value));
      return;
    }
    if (lock) {
      await lock.release();
      await router.push(generatedRecordsTab(matrixPrefix.value));
      return;
    }
    await attachments.discardPending();
    await router.push(matrixPageLocation(matrixPrefix.value, returnFolderCode));
  }

  if (recordId) {
    watch(recordId, load, { immediate: true });
  } else {
    watch([matrixId, () => identifier?.value], () => load(), { immediate: true });
  }

  watch(
    () => authStore.selectedBranchId,
    (next, previous) => {
      if (next !== previous) {
        latestRequest += 1;
        router.replace({ name: 'Home' });
      }
    }
  );

  onBeforeUnmount(() => {
    // The trail override is a module global and the requests outlive the component: a late
    // form or save must find nobody listening.
    latestRequest += 1;
    clearBreadcrumbTrail();
  });

  return {
    isFilling,
    isViewing,
    form,
    loading,
    title,
    matrixPrefix,
    recordLine,
    stages,
    showExportPdf,
    compiled,
    totalFields,
    answers,
    selectedFieldId,
    extraIssues,
    saveAttempted,
    saving,
    hasChanges,
    stageModalOpen,
    indexerModalOpen,
    indexerError,
    indexerChecking,
    clearIndexerError,
    acceptIndexer,
    checkIndexer,
    cancelIndexer,
    correction,
    attachments,
    load,
    setValue,
    setCellValue,
    addExtraRow,
    removeExtraRow,
    locateField,
    save,
    closeStageModal,
    confirmStage,
    cancel,
  };
}
